import { useInView } from '../../../hooks/useInView';

const steps = [
  {
    num: '01',
    icon: 'ri-compass-discover-line',
    title: 'Discovery & Vision Alignment',
    desc: 'We immerse ourselves in your brand, goals, and audience. Every creative decision begins with deep understanding of what your production needs to achieve — and for whom.',
  },
  {
    num: '02',
    icon: 'ri-lightbulb-flash-line',
    title: 'Creative Strategy & Planning',
    desc: 'Scripts, shot lists, locations, casting — every element designed with intent. We build the complete production roadmap before a single camera rolls.',
  },
  {
    num: '03',
    icon: 'ri-movie-line',
    title: 'Production & Filming',
    desc: 'Cinema-grade equipment, experienced broadcast crew, and masterful direction. The day of production is where two decades of strategy meets precision artistry.',
  },
  {
    num: '04',
    icon: 'ri-edit-2-line',
    title: 'Editing & Post-Production',
    desc: 'Color grading, sound design, motion graphics — we refine every frame until the final product exceeds every benchmark set together during pre-production.',
  },
  {
    num: '05',
    icon: 'ri-send-plane-fill',
    title: 'Delivery & Optimization',
    desc: 'Optimized exports for every platform. Strategic guidance on maximizing content impact and reach across digital and broadcast channels.',
  },
];

export default function ProcessSection() {
  const { ref, inView } = useInView();

  return (
    <section className="relative overflow-hidden" id="process" style={{ background: 'linear-gradient(180deg, #080808 0%, #0f0f0f 50%, #080808 100%)' }}>
      {/* Decorative vertical center line */}
      <div className="absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-gold/8 to-transparent hidden lg:block" />

      <div className="max-w-7xl mx-auto px-8 lg:px-20 py-36">
        {/* Header */}
        <div
          ref={ref as React.RefObject<HTMLDivElement>}
          className={`text-center mb-28 transition-all duration-800 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        >
          <p className="font-sans text-[0.58rem] tracking-[0.45em] uppercase text-gold mb-6 font-medium">How We Work</p>
          <h2 className="font-serif font-bold text-white leading-tight" style={{ fontSize: 'clamp(2.8rem, 5vw, 5rem)', letterSpacing: '-0.02em' }}>
            A Refined Production<br />
            <em className="not-italic text-gold">Experience</em>
          </h2>
          <p className="font-sans text-sm text-white/35 mt-6 max-w-md mx-auto leading-relaxed">
            Every production follows a proven 5-stage methodology perfected over 20+ years of broadcast-level work.
          </p>
        </div>

        {/* Steps — large alternating vertical layout */}
        <div className="relative flex flex-col gap-0 max-w-4xl mx-auto">
          {steps.map((step, i) => {
            const isLeft = i % 2 === 0;
            return (
              <div
                key={step.num}
                className={`relative grid grid-cols-1 lg:grid-cols-2 gap-0 transition-all duration-800 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                style={{ transitionDelay: `${i * 120}ms` }}
              >
                {/* Spacer for right-aligned items */}
                {!isLeft && <div className="hidden lg:block" />}

                {/* Content card */}
                <div className={`relative p-10 lg:p-12 border border-white/5 hover:border-gold/20 bg-white/[0.015] hover:bg-white/[0.03] transition-all duration-400 group cursor-default mb-4 lg:mb-6 ${isLeft ? 'lg:mr-10' : 'lg:ml-10'}`}>
                  {/* Step number - large ghost */}
                  <div className="absolute top-6 right-8 font-serif font-bold text-white/4 group-hover:text-white/7 transition-colors duration-500 leading-none" style={{ fontSize: '5rem' }}>
                    {step.num}
                  </div>

                  {/* Icon */}
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-11 h-11 flex items-center justify-center border border-gold/25 group-hover:border-gold/60 bg-gold/5 transition-colors duration-300 flex-shrink-0">
                      <i className={`${step.icon} text-gold text-lg`} />
                    </div>
                    <div className="w-10 h-px bg-gold/25 group-hover:bg-gold/50 transition-colors duration-300" />
                    <span className="font-sans text-[0.55rem] tracking-[0.35em] uppercase text-gold/40 font-medium">{step.num}</span>
                  </div>

                  <h3 className="font-serif text-2xl font-bold text-white leading-snug mb-4">{step.title}</h3>
                  <p className="font-sans text-sm text-white/45 leading-[1.9] group-hover:text-white/65 transition-colors duration-300">{step.desc}</p>
                </div>

                {/* Spacer for left-aligned items */}
                {isLeft && <div className="hidden lg:block" />}
              </div>
            );
          })}
        </div>

        {/* Full-width cinematic image footer */}
        <div
          className={`mt-24 relative h-72 overflow-hidden transition-all duration-800 delay-500 ${inView ? 'opacity-100' : 'opacity-0'}`}
        >
          <img
            src="https://public.readdy.ai/ai/img_res/79c465558293727a79f5ae210f36fb02.jpg"
            alt="Production team at work"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#080808] via-[#080808]/20 to-[#080808]/70" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <div className="flex items-center justify-center gap-4 mb-4">
                <div className="w-10 h-px bg-gold/50" />
                <i className="ri-film-line text-gold text-lg" />
                <div className="w-10 h-px bg-gold/50" />
              </div>
              <p className="font-serif text-2xl text-white/65 italic">
                &ldquo;From concept to completion — every frame matters.&rdquo;
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
